/**
 * NotificationWidget - Bell icon dropdown showing recent notifications
 * Displays badge with unread count, dropdown with notification list
 */

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Button,
} from '@/components/ui';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import { Bell, Newspaper } from 'lucide-react';
import { useNotificationWidget } from './NotificationWidget.hook';
import { timeAgo } from './notification-time-ago.util';
import { navigatePortalInternal } from '../PortalLayout/PortalLayout.store';

export interface NotificationWidgetProps {
  className?: string;
}

export function NotificationWidget({ className }: NotificationWidgetProps) {
  const { t } = useTranslation(NS.COMMON);
  const { notifications, badgeCount, loading, markAsOpened } = useNotificationWidget();

  const handleViewAll = () => {
    navigatePortalInternal('/news-feed');
  };

  const handleViewNotification = (id: string) => {
    navigatePortalInternal(`/notification/view/${id}`);
  };

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) markAsOpened();
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={className ?? 'relative h-8 w-8 p-0'}
          aria-label={t('NotificationWidget_BellLabel', { defaultValue: 'Notifications' })}
        >
          <Bell className="h-5 w-5" />
          {badgeCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
              {badgeCount > 99 ? '99+' : badgeCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-72" align="end">
        {/* View All link */}
        <DropdownMenuItem onClick={handleViewAll} className="cursor-pointer">
          <Newspaper className="mr-2 h-4 w-4" />
          <span>{t('NotificationWidget_ViewAll', { defaultValue: 'View All Notifications' })}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Loading state */}
        {loading && (
          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
            {t('NotificationWidget_Loading', { defaultValue: 'Loading...' })}
          </div>
        )}

        {/* Empty state */}
        {!loading && notifications.length === 0 && (
          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
            {t('NotificationWidget_Empty', { defaultValue: 'No notifications' })}
          </div>
        )}

        {/* Notification items */}
        {!loading &&
          notifications.map((notification) => (
            <DropdownMenuItem
              key={notification.id}
              onClick={() => handleViewNotification(notification.id)}
              className="cursor-pointer flex items-center justify-between gap-2"
            >
              <span className="truncate text-sm italic">{notification.title}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {timeAgo(notification.timestamp, t)}
              </span>
            </DropdownMenuItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
